<script lang="ts">
	import {Utils} from "./util";
	import {translations} from "./config";
	import type {Community, Region} from "./model";
	import MapSelect from "./Select.svelte";
	import type {Option} from "./option";
	import Map from "./Map.svelte";
	import Legend from "./Legend.svelte";
	import type {Color} from "./map";
	import Filters from "./Filters.svelte";
	import Questions from "./Questions.svelte";
	import Hover from "./Hover.svelte";
	import Print from "./Print.svelte";
	import XLSX, {CellObject} from "xlsx-js-style";
	import {Answer, Question} from "./model";

	let hoverCommunity: Community | null;
	let mouseX = 0;
	let mouseY = 0;

	let communityData: Community;

	let isPhoneSize: boolean;
	checkPhoneSize();

	let selectedRegion = 3;
	let regions: Region[] = [];
	let communities: { [key: string]: Community } = {};
	let regionOptions: Option[] = [];
	const questions = Utils.get<Question[]>('/api/api/mega/public/question');

	let selectedQuestionAnswers: { [id: number]: boolean };
	let selectedElection: number | null = null;

	let filteredCommunities: Community[];
	let markings: { [key: string]: 'dotted' | 'striped' } = {};
	let fills: { [key: string]: Color } = {};
	let lastClicked: string;

	let showPrint = false;
	// question / answer filter
	$: {
		if (selectedQuestionAnswers || selectedElection) {
			filterCommunities(selectedQuestionAnswers || {}, selectedElection).then(communities => {
				const f = {};
				const m = {};
				for (const c of communities) {
					// if (selectedQuestion && selectedAnswer != null)
					// 	f[c.key] = selectedAnswer ? '#575756' : '#FE6D50';
					f[c.key] = '#FE6D50';
					if (selectedElection) {
						if (selectedElection == -3000 && c.citizens < 3000) {
							m[c.key] = 'striped';
						}
						if (selectedElection == +2999 && c.citizens > 2999) {
							m[c.key] = 'dotted';
						}
					}
				}

				filteredCommunities = communities;
				markings = m;
				fills = f;
			});
		} else {
			fills = {};
			markings = {};
		}
	}

	/** Cast to region */
	function r(r: any): Region {
		return r;
	}

	function checkPhoneSize() {
		const listener = v => {
			isPhoneSize = v.matches;
		}
		const mq = window.matchMedia("(max-width: 500px)");
		isPhoneSize = mq.matches;
		mq.addEventListener
			? mq.addEventListener("change", listener)
			: mq.addListener(listener);
	}

	function communityHover(options: CustomEvent<{ key: string; e: MouseEvent }>) {
		mouseX = options.detail.e.pageX;
		mouseY = options.detail.e.pageY;

		hoverCommunity = communities[options.detail.key];
	}

	function communityUnhover(event: CustomEvent<string>) {
		hoverCommunity = null;
	}

	async function communityClicked(event: CustomEvent<string>) {
		communityData = await Utils.get(`/api/api/mega/public/community/${event.detail}/questions`);
	}

	async function filterCommunities(questionAnswers: { [id: number]: boolean }, citizens?: number) {
		console.log('filtering');
		let url = '/api/api/mega/public/community';
		const params = [];
		for (const question of Object.keys(questionAnswers)) {
			const value = questionAnswers[question];
			if (question && value != null) {
				const answer = value ? '1' : '0';
				params.push(`filter%5Bhas_answered%5D%5B${question}%5D=${answer}`);
				// params.push(`filter%5Bhas_answered%5D=${question}%2C${answer}`);
			}
		}
		// disabled for now, since the filtering logic changed
		// if (citizens != null) {
		// 	params.push(`&filter%5Bcitizens%5D=${citizens}`);
		// }
		if (params.length == 0) {
			return [];
		}
		return Utils.get<Community[]>(`${url}?${params.join('&')}`);
	}

	Utils.get<Region[]>('/api/api/mega/public/regions').then(async r => {
		regions = r;
		if (regions == null)
			return;

		const comms = {};
		for (const region of regions) {
			for (const community of region.communities) {
				comms[community.key] = community;
			}
		}
		communities = comms;
		filteredCommunities = Object.keys(communities).map(key => communities[key]);

		regionOptions = regions.map(r => ({
			label: Utils.chooseTranslation(r.name),
			value: r.id
		}));
		selectedRegion = regionOptions[3]?.value;
	});

	/** Map over all answers that are not disabled */
	function mapAnswers<T>(answers: Answer[], map: (question: string, value: boolean, additionalInfo: { [lang: string]: string }) => T): T[] {
		return answers
			.filter(a => !a.isDisabled)
			.map(a => map(Utils.chooseTranslation(a.question.name), a.value, a.additionalInfo));
	}

	async function onCSVClick() {
		// STEP 1: Create a new workbook
		const wb = XLSX.utils.book_new();

		// color theme
		const orange = "FE6D50";
		const blue = "AAEAED";
		const blueFill = {fill: {fgColor: {rgb: blue}}};
		const orangeFill = {fill: {fgColor: {rgb: orange}}};
		const border = {style: "thin"};
		const bottomBorder = {border: {bottom: border}};

		const q = await questions;

		const questionAnswers = Object.keys(selectedQuestionAnswers)
			.map(id => {
				return [
					{v: Utils.chooseTranslation(q.find(q => q.id == id)?.name), t: "s", s: blueFill},
					{v: !!selectedQuestionAnswers[id], t: "b", s: blueFill}
				] as [CellObject, CellObject];
			});
		const communityElections: CellObject[][] = filteredCommunities
			.map(c => [
				{v: Utils.chooseTranslation(c.name), t: "s", s: blueFill},
				{
					v: Utils.chooseTranslation(c.citizens < 3000 ? translations.voting.relative : translations.voting.proportional),
					t: "s",
					s: orangeFill
				},
			]);

		const filterSheet: CellObject[][] = [
			[
				{v: Utils.chooseTranslation(translations.question), t: "s", s: {...blueFill, ...bottomBorder}},
				{v: Utils.chooseTranslation(translations.answer), t: "s", s: {...blueFill, ...bottomBorder}},
			],
			...questionAnswers,
			[], // empty row
			[
				{v: Utils.chooseTranslation(translations.community), t: "s", s: {...blueFill, ...bottomBorder}},
				{v: Utils.chooseTranslation(translations.voting.label), t: "s", s: {...orangeFill, ...bottomBorder}},
			],
			...communityElections
		];
		const electionStart = questionAnswers.length + 3; // + 3 because of first header row, empty row and because it's 1 based (not 0)

		// add worksheets
		const ws = XLSX.utils.aoa_to_sheet(filterSheet);
		ws['!autofilter'] = {ref: `A${electionStart}:B${electionStart + communityElections.length}`};
		ws['!cols'] = [{width: 60}, {width: 40}];

		XLSX.utils.book_append_sheet(wb, ws, "Filters");

		// if community is selected, add questions sheet
		if (communityData) {
			const questions = mapAnswers(communityData.public_answers, (question, value, additionalInfo) => {
				const row: CellObject[] = [
					{v: question, t: "s", s: blueFill},
					{v: !!value, t: "b", s: blueFill},
				];

				if (additionalInfo != null && Object.keys(additionalInfo).length > 0)
					row.push({v: Utils.extractText(Utils.chooseTranslation(additionalInfo)), t: "s", s: blueFill});

				return row;
			});

			const questionSheet: CellObject[][] = [
				[
					{v: Utils.chooseTranslation(translations.question), t: "s", s: {...orangeFill, ...bottomBorder}},
					{v: Utils.chooseTranslation(translations.answer), t: "s", s: {...orangeFill, ...bottomBorder}},
					{
						v: Utils.chooseTranslation(translations.additionalInfo),
						t: "s",
						s: {...orangeFill, ...bottomBorder}
					},
				],
				...questions
			];

			const ws2 = XLSX.utils.aoa_to_sheet(questionSheet);
			ws2['!cols'] = [{width: 50}, {width: 15}, {width: 50}];
			XLSX.utils.book_append_sheet(wb, ws2, Utils.chooseTranslation(communityData.name));
		}

		// download
		XLSX.writeFile(wb, "statistics.xlsx");
	}
</script>

<svelte:options immutable={true}/>

<div id="svgmap-content" class="grid">
    <div id="svgmap-container" class="col">
        {#if isPhoneSize}
            <MapSelect bind:value={selectedRegion} options={regionOptions}/>
        {/if}

	    {#if isPhoneSize}
            <div class="phone-filters">
                {#await questions}
                    {Utils.chooseTranslation(translations.loadingQuestions)}
                {:then questions}
                    <Filters bind:selectedQuestionAnswers={selectedQuestionAnswers}
                             bind:selectedElection={selectedElection}
                             {isPhoneSize}
                             questionOptions={questions.map(q => ({
                                id: q.id,
                                label: String(Utils.chooseTranslation(q.name)),
                            }))}
                    />

                {/await}
            </div>

		    {#each regions as region}
                {#if r(region).id === selectedRegion}
                    <Map path="./map/img/map_{r(region).id}.svg" {regions} {fills} {markings} bind:lastClicked
                         on:communityClick={communityClicked} on:communityHover={communityHover}
                         on:communityUnhover={communityUnhover}/>
                {/if}
            {/each}
        {:else}
            <Map path="./map/img/map.svg" {regions} {fills} {markings} bind:lastClicked
                 on:communityClick={communityClicked} on:communityHover={communityHover}
                 on:communityUnhover={communityUnhover}/>
        {/if}
	    <!--        <img class="logo" height="32" src="./map/img/logo.svg">-->
	    <!--                <img id="community-logo" height="32">-->
    </div>
    <div class="col flex-column">
        {#if !isPhoneSize}
            {#await questions}
                {Utils.chooseTranslation(translations.loadingQuestions)}
            {:then questions}
                <Filters bind:selectedQuestionAnswers bind:selectedElection
                         {isPhoneSize}
                         questionOptions={questions.map(q => ({
                                id: q.id,
                                label: String(Utils.chooseTranslation(q.name)),
                            }))}
                />
            {/await}
        {/if}
	    <Questions {communityData}/>
        <button id="csvButton" class="btn-brand"
                on:click={onCSVClick}>
            <!-- style="display: {communityData?.public_answers?.length > 0 ? '' : 'none'}" -->
	        <!-- removed because it now also exports filters -->
	        {Utils.chooseTranslation(translations.csv)}
        </button>
        <button class="btn-brand"
                on:click={() => showPrint = true}>{Utils.chooseTranslation(translations.print)}</button>
        <div id="placeholder" style="display: {communityData ? 'none' : 'unset'}">
            <h2 id="placeholderText">
                {Utils.chooseTranslation(translations.placeholderText)}
            </h2>
            <div class="placeholder-img">
                <img alt="Mouse gesture" id="gesture" src="./map/img/gesture.png">
            </div>
        </div>
        <div class="legend">
            <Legend/>
        </div>
    </div>
</div>
<Hover {hoverCommunity} {mouseX} {mouseY}/>

{#if showPrint}
    {#await questions}
        {Utils.chooseTranslation(translations.loadingQuestions)}
    {:then questions}
        <Print {regions} {communityData} {fills} {markings} {lastClicked} {selectedQuestionAnswers} {questions} on:done={() => showPrint = false}/>
    {/await}
{/if}

<style lang="scss">
  .flex-column {
    display: flex;
    flex-direction: column;
    align-items: start;
  }

  .legend {
    margin-top: 2rem;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: end;
  }

  .phone-filters {
    margin-top: 4.5rem;
  }
</style>
